import React, {useCallback, useMemo} from 'react';
import {View} from 'react-native';
import {ViewProps} from './model';
import {useSelector} from 'react-redux';
import {useTheme} from '../../../../../shared/theme';
import {useIsDesktop} from '../../../../../shared/hooks/useIsDesktop';
import {currentLocalitySelector} from '../../../../../redux/selectors/localities';
import {useDimensions} from '../../../../../shared/hooks/useDimensions';
import {useStyles} from '../../../../../shared/hooks/styles';
import {DeviceSize} from '../../../../../shared/theme/helpers';
import {formatPhone, formatPhoneToCallable} from '../../../../../shared/pipes';
import {images} from '../../../../../assets/icons';
import {YaMap} from '../../../../../shared/components/yaMap';

export default ({productAcceptors, activeAcceptorBranch, onClickAcceptorBranch, product, mapProps}: ViewProps) => {
  const theme = useTheme();
  const isDesktop = useIsDesktop();
  const currentLocality = useSelector(currentLocalitySelector());
  const {height} = useDimensions();

  const mapHeight = height * 0.5;

  const styles = useStyles(({addShadow, mediaOnly}) => ({
    container: {
      ...mediaOnly(
        DeviceSize.desktop,
        {
          borderRadius: 40,
          flexDirection: 'row',
          alignItems: 'stretch',
          ...addShadow({
            opacity: 0.05,
            width: 0,
            height: 4,
            radius: 10,
          }),
        },
        {
          borderRadius: 32,
        },
      ),
    },
    map: mediaOnly(DeviceSize.desktop, {
      minHeight: 650,
      flexShrink: 1,
      flexGrow: 1,
    }),
    image: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 1,
    },
  }));

  const centerPoint = useMemo(() => currentLocality?.geolocation, [currentLocality]);

  const markers = useMemo(
    () =>
      productAcceptors.map((item, i) => {
        const coordinates = item.coordinates.map((coordinate) => Number(coordinate));
        return {
          id: coordinates[0] + coordinates[1] + i,
          payload: item,
          coordinates: coordinates ?? [0, 0],
        };
      }),
    [productAcceptors],
  );

  const handleClickMarker = useCallback((data) => onClickAcceptorBranch(data.payload), [onClickAcceptorBranch]);
  const handleNullacceptorBranch = useCallback(() => onClickAcceptorBranch(null), [onClickAcceptorBranch]);

  const mapDetails = useMemo(() => {
    return [
      {
        title: activeAcceptorBranch?.shortName || product?.name || '',
        info: [
          {
            text: activeAcceptorBranch?.address ?? '',
            icon: <images.Location color={theme.color.primaryColor} />,
            show: !!activeAcceptorBranch?.address,
          },
          {
            text: formatPhone(activeAcceptorBranch?.contactPhone),
            href: `tel:${formatPhoneToCallable(activeAcceptorBranch?.contactPhone)}`,
            icon: <images.Phone color={theme.color.primaryColor} />,
            show: !!activeAcceptorBranch?.contactPhone,
          },
        ],
        show: !!activeAcceptorBranch,
      },

      {
        title: 'Часы работы',
        info: [
          {
            text: activeAcceptorBranch?.workTime ?? '',
            icon: <images.Clock2 color={theme.color.primaryColor} />,
          },
        ],
        show: !!activeAcceptorBranch?.workTime,
      },

      {
        title: 'Количество',
        info: [
          {
            text: `${activeAcceptorBranch?.itemsRemain} шт.`,
            icon: <images.Basket color={theme.color.primaryColor} />,
          },
        ],
        show: !!activeAcceptorBranch?.itemsRemain,
      },
    ];
  }, [activeAcceptorBranch]);

  return (
    <View style={styles.container}>
      <View style={[styles.map, !isDesktop ? {height: mapHeight} : {}]}>
        <YaMap
          markers={markers}
          centerPoint={centerPoint}
          onPressMarker={handleClickMarker}
          isDetailsVisible={!!activeAcceptorBranch}
          onCloseDetails={handleNullacceptorBranch}
          details={mapDetails}
          {...mapProps}
        />
      </View>
      {isDesktop && (
        <View style={styles.image}>
          <images.AcceptorMap width={310} height={298} color={theme.polls.shapes.color} />
        </View>
      )}
    </View>
  );
};
